import { ActionFunction, data, Form, useNavigation } from 'react-router';
import { isDesktopApp } from '@todesktop/client-core/platform/todesktop';
// import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import { FaGoogle } from 'react-icons/fa';
import { MailIcon } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@/components/ui/dialog';
import { Route } from './+types/signin';

export const loader = async ({ request }: { request: Request }) => {
  const isTest = process.env.NODE_ENV === 'test';
  return { isTest };
};

export const action: ActionFunction = async ({ request }) => {
  const formData = await request.formData();
  const email = formData.get('email');
  const magicLink = formData.get('magicLink');

  if (magicLink) {
    const apiUrl = process.env.VITE_API_URL;
    const response = await fetch(`${apiUrl}/users/magic_link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (response.ok) {
      return data({ success: true, message: 'Magic link sent to your email' });
    } else {
      return data({ success: false, message: 'Failed to send magic link' });
    }
  }

  return null;
};

export default function Signin({ loaderData }: Route.ComponentProps) {
  const { isTest } = loaderData;
  console.log('isTest', isTest);
  const navigation = useNavigation();
  const [showDialog, setShowDialog] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiUrl = import.meta.env.VITE_API_URL;
  const googleAuthUrl = `${apiUrl}/users/auth/google_oauth2`;

  useEffect(() => {
    try {
      posthog.reset();
    } catch (error) {
      console.error('Error identifying user:', error);
    }
  }, []);

  const onClose = () => setShowDialog(false);

  const handleMagicLinkClick = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }
    setMagicLinkLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/users/magic_link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setShowDialog(true);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to send magic link');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('Magic link error:', err);
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const isDesktop = isDesktopApp();

  return (
    <div className="flex items-center justify-center min-h-screen bg-background-tertiary">
      {/* <Avatar
        src="/logo.png"
        alt="Logo"
        className="w-10 h-10 absolute top-6 left-6"
      /> */}
      <Card className="w-[350px]">
        <CardHeader>
          <CardTitle>Sign In</CardTitle>
        </CardHeader>
        <CardContent>
          {isTest || isDesktop ? (
            <form method="post" action={`${apiUrl}/users/sign_in`}>
              <div className="space-y-2">
                <Input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Input
                  type="password"
                  name="password"
                  placeholder="Enter your password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <Button type="submit" className="w-full mt-4">
                Sign In
              </Button>
            </form>
          ) : (
            <Form method="post">
              <Input
                type="email"
                name="email"
                placeholder="Enter your email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button
                onClick={handleMagicLinkClick}
                disabled={magicLinkLoading}
                className="w-full mt-4"
              >
                {magicLinkLoading ? 'Sending...' : 'Email Magic Link'}
              </Button>
            </Form>
          )}

          {error && (
            <p className="mt-2 text-sm text-center text-red-500">{error}</p>
          )}
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <a href={googleAuthUrl} className="w-full">
            <Button
              variant="outline"
              className="w-full flex items-center justify-center space-x-2"
            >
              <FaGoogle className="w-5 h-5" />
              <span>Sign in with Google</span>
            </Button>
          </a>
        </CardFooter>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>Check Your Email</DialogHeader>
          <DialogBody>
            <div className="">
              <h2 className="text-2xl font-semibold tracking-tight text-center">
                We've emailed you a magic link.
              </h2>

              <p className="text-center mt-4 mb-1">
                Click the link in the email to sign in to your account.
              </p>

              <p className="text-sm text-muted-foreground text-center">
                If you don't see the email, please check your spam folder.
              </p>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button onClick={onClose} className="w-full">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
